import mixpanel from "mixpanel-browser";

import { MixpanelProjectToken } from "../utils/env";

interface OfflineEvent {
  event: string;
  props: object;
  time: number;
}

interface TrackRTCPeerConnectionFailureProps {
  isInitialStartupIssue: boolean;
  isInternetConnectionIssue: boolean;
}

class AnalyticsService {
  private offlineEventsQueue: OfflineEvent[] = [];
  private isWaitingForInternet = false;

  private onInternetRestored = () => {
    for (const { event, props, time } of this.offlineEventsQueue) {
      this.track(event, { ...props, time });
    }
    this.offlineEventsQueue = [];
    this.isWaitingForInternet = false;
    window.removeEventListener('online', this.onInternetRestored);
  }

  init() {
    mixpanel.init(MixpanelProjectToken);
  }

  identify(id: any) {
    mixpanel.identify(id);
  }

  track(event: string, props: object) {
    // note: in some cases (e.g. when user connects to a virtual adapter, or a mobile hotspot),
    // the `onLine` property might return true, cause the user is connected to some metwork,
    // but they might not actually have access to the internet;
    // this is, by and large, an edge case, but if we need, we might check online status by sending 
    // request to an arbitrary Verbit endpoint (we decided against this for now, as it would
    // create unnecessary traffic, both for user and our servers, and can have negative impact 
    // on performance)
    if (window.navigator.onLine) {
      mixpanel.track(event, props);
    } else {
      this.offlineEventsQueue.push({
        event,
        props,
        time: Math.round(Date.now() / 1000),
      });
      if (!this.isWaitingForInternet) {
        window.addEventListener('online', this.onInternetRestored);
        this.isWaitingForInternet = true;
      }
    }
  }

  trackPageVisit(props: any) {
    this.track("On-Site - Page Visit", props);
  }

  trackStreamStart(props: any) {
    this.track("On-Site - user started streaming", props);
  }

  trackResumeStream(props: any) {
    this.track("On-Site - use resumed streaming", props);
  }

  trackStreamPause(props: any) {
    this.track("On-Site - user paused streaming", props);
  }

  trackRTCPeerConnectionFailure(props: TrackRTCPeerConnectionFailureProps) {
    this.track("On-Site - RTCPeerConnection failed", props);
  }

  trackRTCPeerConnectionRestored() {
    this.track("On-Site - RTCPeerConnection restored", {});
  }
}

export const analyticsService = new AnalyticsService();
